<template>
  <main class="page">
    <template v-if="!loading">
      <div class="header">
        <div>
          <h1 class="title">
            Historia <br />
            zgłoszeń
          </h1>
          <button
            v-if="isAtLeastOneInterrupted"
            class="retry-button"
            @click="retryAll"
          >
            <status-component type="interrupted" />
            <span>Ponów wysyłanie</span>
          </button>
        </div>
        <flight-component :flight="flight" class="header__flight" read-only />
      </div>

      <div v-if="isNotEmpty" class="table" role="table">
        <div class="table-header" role="rowgroup">
          <div class="table-row" role="table">
            <span class="table-title" role="columnheader">Imię</span>
            <span class="table-title" role="columnheader">Nazwisko</span>
            <span class="table-title" role="columnheader">Telefon</span>
          </div>
        </div>

        <div
          v-for="request in requests"
          :key="request.id"
          role="rowgroup"
          @click="toggleActive(request.id)"
        >
          <div class="table-row" role="row">
            <span role="cell" class="table-content">{{
              request.data.first_name
            }}</span>
            <span role="cell" class="table-content">{{
              request.data.last_name
            }}</span>
            <span role="cell" class="table-content">{{
              request.data.phone
            }}</span>
            <span role="cell" class="table-content">
              <status-component :type="request.status" />
            </span>
            <span role="cell" class="table-content">
              <button
                :class="{ 'toggle-button--active': isActive(request.id) }"
                :aria-expanded="isActive(request.id)"
                :aria-controls="`details-${request.id}`"
                class="toggle-button"
                aria-label="Rozwiń"
              >
                <img
                  src="/svg/history/chevron-down.svg"
                  alt=""
                  width="24"
                  height="24"
                />
              </button>
            </span>
          </div>
          <details-component
            v-show="isActive(request.id)"
            :aria-hidden="isActive(request.id)"
            :id="`details-${request.id}`"
            :request="request"
          />
        </div>
      </div>
      <div v-else class="empty">Brak zgłoszeń dla wybranego lotu.</div>
    </template>
    <div v-else class="loader-wrapper">
      <loader />
    </div>
  </main>
</template>

<script>
import StatusComponent from '@/components/requests-history/Status'
import DetailsComponent from '@/components/requests-history/Details'
import FlightComponent from '@/components/Flight'
import service from '@/service'
import Loader from '@/components/Loader'
import { LeadStatus } from '@/constants'

const isRetryable = request =>
  [LeadStatus.INTERRUPTED, LeadStatus.FAILED].includes(request.status)

export default {
  components: {
    Loader,
    StatusComponent,
    DetailsComponent,
    FlightComponent
  },
  data() {
    return {
      loading: true,
      flight: null
    }
  },
  computed: {
    isAtLeastOneInterrupted() {
      return this.requests.some(isRetryable)
    },
    requests() {
      return this.$store.state.leads.items.filter(
        item => String(item.data.flight) === String(this.$route.params.flightId)
      )
    },
    isNotEmpty() {
      return this.requests.length > 0
    },
    isActive() {
      return id => id === this.$route.query.lead
    }
  },
  created() {
    const { flightId } = this.$route.params

    if (!flightId) this.$router.push({ name: 'not-found' })

    service.flights
      .byId(flightId)
      .then(({ data }) => {
        if (data.error) this.$router.push({ name: 'not-found' })
        else this.flight = data.flight
      })
      .catch(err => {
        if (err.response && err.response.data)
          this.$router.push({ name: 'not-found' })
        else throw err
      })
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    toggleActive(id) {
      this.$router.replace({
        query: {
          lead: this.$route.query.lead === id ? undefined : id
        }
      })
    },
    retryAll() {
      this.requests.filter(isRetryable).forEach(request => {
        this.$store.dispatch('createLead', request)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.header {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 24px;

  &__flight {
    width: 100%;
  }

  @include media-up(md) {
    grid-template-columns: 1fr 1fr;
  }
}

.retry-button {
  @include reset-button;
  color: $status-interrupted;
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  margin-left: -16px;
  margin-top: 4px;

  span {
    margin-left: 8px;
  }
}

.table {
  margin-top: 40px;
}

.table-title {
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-row {
  display: grid;
  padding: 6px 0;
  align-items: center;
  grid-template-columns: 64px 88px 128px 1fr 44px;

  @include media-up(lg) {
    grid-template-columns: 88px 104px 144px 1fr 44px;
  }
}

.table-content {
  overflow: hidden;
  text-overflow: ellipsis;

  &:nth-child(4) {
    justify-self: end;
    padding: 0 8px;
  }
}

.toggle-button {
  @include reset-button;
  @include center-content;
  @include size(44px);
  transition: transform $base-transition;

  &--active {
    transform: rotate(180deg);
  }

  img {
    display: block;
  }
}

.empty {
  text-align: center;
  padding: 48px 0 24px;
}

.loader-wrapper {
  @include center-content;
  padding: 48px 0 32px;
  min-height: 50vh;
}
</style>
